import { useEffect, useState, useCallback } from "react";
import { HITL_BASE_URL } from "../apiConfig";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Box,
} from "@mui/material";
import type { AlertColor } from "@mui/material/Alert";

interface PendingApproval {
  id: number;
  approval_id: string;
  agent_id: string;
  agent_name: string;
  action_type: string;
  action_description?: string;
  proposed_action: Record<string, any>;
  reasoning?: string;
  confidence_score: number;
  impact_level: string;
  estimated_cost?: number;
  affected_entities?: Record<string, any> | null;
  status: string;
  priority: string;
  created_at: string;
  expires_at?: string;
  reviewed_by?: string;
  review_notes?: string;
  reviewed_at?: string;
}

interface ApprovalStats {
  pending: number;
  approved: number;
  rejected: number;
  total_decisions: number;
  approval_rate: number;
}

function formatTimeAgo(dateStr: string) {
  const d = new Date(dateStr);
  const now = new Date();
  const diffSec = (now.getTime() - d.getTime()) / 1000;
  if (Number.isNaN(diffSec)) return dateStr || "";
  if (diffSec < 60) return "Just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hours ago`;
  return d.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const ImpactBadge = ({ level }: { level: string }) => {
  const colors: Record<string, { bg: string; text: string }> = {
    LOW: { bg: "bg-blue-100", text: "text-blue-800" },
    MEDIUM: { bg: "bg-yellow-100", text: "text-yellow-800" },
    HIGH: { bg: "bg-orange-100", text: "text-orange-800" },
    CRITICAL: { bg: "bg-red-100", text: "text-red-800" },
  };
  const color = colors[level] || colors.LOW;
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color.bg} ${color.text}`}
    >
      {level}
    </span>
  );
};

const PriorityBadge = ({ priority }: { priority: string }) => {
  const colors: Record<string, { bg: string; text: string }> = {
    low: { bg: "bg-slate-100", text: "text-slate-700" },
    normal: { bg: "bg-blue-100", text: "text-blue-700" },
    high: { bg: "bg-orange-100", text: "text-orange-700" },
    urgent: { bg: "bg-red-100", text: "text-red-700" },
  };
  const color = colors[priority] || colors.normal;
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color.bg} ${color.text}`}
    >
      {priority.toUpperCase()}
    </span>
  );
};

const ConfidenceBar = ({ score }: { score: number }) => {
  const percentage = Math.round(score * 100);
  const color =
    score >= 0.8 ? "bg-green-500" : score >= 0.6 ? "bg-yellow-500" : "bg-red-500";

  return (
    <div className="w-full">
      <div className="flex justify-between text-xs text-slate-600 mb-1">
        <span>Confidence</span>
        <span className="font-medium">{percentage}%</span>
      </div>
      <div className="w-full bg-slate-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full ${color} transition-all`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Excel Export Tab Component
const ExcelExportTab = () => {
  const [selectedStore, setSelectedStore] = useState('all');
  const [loading, setLoading] = useState(false);
  
  const handleExportExcel = async () => {
    setLoading(true);
    try {
      const params = selectedStore !== 'all' ? `?store_id=${selectedStore}` : '';
      const res = await fetch(`${HITL_BASE_URL}/api/hitl/export/excel${params}`);
      if (!res.ok) throw new Error('Export failed');
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `approvals_${selectedStore}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      alert('Excel export completed successfully');
    } catch (error) {
      alert('Excel export failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
        <h3 className="text-xl font-bold text-green-800 mb-2">Dynamic Excel Export</h3>
        <p className="text-green-700 mb-4">Export store-specific approval data to Excel with dynamic filtering</p>
        
        <div className="flex gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-green-800 mb-2">Select Store</label>
            <select 
              value={selectedStore} 
              onChange={(e) => setSelectedStore(e.target.value)}
              className="px-3 py-2 border border-green-300 rounded-lg bg-white text-black"
            >
              <option value="all">All Stores</option>
              <option value="S001">Store S001</option>
              <option value="S002">Store S002</option>
              <option value="S003">Store S003</option>
            </select>
          </div>
          <button 
            onClick={handleExportExcel}
            disabled={loading}
            className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:opacity-50"
          >
            {loading ? 'Exporting...' : 'Export to Excel'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Hallucination Detection Tab Component
const HallucinationDetectionTab = () => {
  const [selectedStore, setSelectedStore] = useState('all');
  const [approvals, setApprovals] = useState<PendingApproval[]>([]);
  const [loading, setLoading] = useState(false);
  
  const fetchLowConfidenceApprovals = async () => {
    setLoading(true);
    try {
      const params = selectedStore !== 'all' ? `?store_id=${selectedStore}&confidence_threshold=0.6` : '?confidence_threshold=0.6';
      const res = await fetch(`${HITL_BASE_URL}/api/agents/approvals/pending${params}`);
      if (!res.ok) throw new Error('Fetch failed');
      const data = await res.json();
      setApprovals((data.pending_approvals || []).filter((a: any) => a.confidence_score < 0.6));
    } catch (error) {
      console.error('Error fetching low confidence approvals:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLowConfidenceApprovals();
  }, [selectedStore]);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-6 rounded-xl border border-orange-200">
        <h3 className="text-xl font-bold text-orange-800 mb-2">Hallucination Detection & Accuracy</h3>
        <p className="text-orange-700 mb-4">Monitor and identify potential AI hallucinations by store</p>
        
        <div className="flex gap-4 items-end mb-4">
          <div>
            <label className="block text-sm font-medium text-orange-800 mb-2">Select Store</label>
            <select 
              value={selectedStore} 
              onChange={(e) => setSelectedStore(e.target.value)}
              className="px-3 py-2 border border-orange-300 rounded-lg bg-white text-black"
            >
              <option value="all">All Stores</option>
              <option value="S001">Store S001</option>
              <option value="S002">Store S002</option>
              <option value="S003">Store S003</option>
            </select>
          </div>
          <button onClick={fetchLowConfidenceApprovals} className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg">
            Refresh
          </button>
        </div>
        
        {loading ? (
          <CircularProgress />
        ) : (
          <div className="space-y-3">
            <h4 className="font-semibold text-orange-800">Low Confidence Actions (&lt; 60%)</h4>
            {approvals.length === 0 ? (
              <p className="text-orange-600">No low confidence actions found</p>
            ) : (
              approvals.map((approval) => (
                <div key={approval.approval_id} className="bg-white p-4 rounded-lg border border-orange-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <h5 className="font-medium">{approval.agent_name}</h5>
                      <p className="text-sm text-gray-600">{approval.action_type}</p>
                      <p className="text-sm text-orange-600">Store: {approval.affected_entities?.store_id || 'N/A'}</p>
                    </div>
                    <div className="text-right">
                      <ConfidenceBar score={approval.confidence_score} />
                      <div className="text-xs text-red-600 font-medium mt-1">
                        ⚠️ Potential hallucination risk
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Autonomous Actions Tab Component
const AutonomousActionsTab = () => {
  const [selectedStore, setSelectedStore] = useState('all');
  const [lastAction, setLastAction] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  
  const fetchLastAction = async () => {
    setLoading(true);
    try {
      const params = selectedStore !== 'all' ? `?store_id=${selectedStore}` : '';
      const res = await fetch(`${HITL_BASE_URL}/api/agents/autonomous/last${params}`);
      if (res.ok) {
        const data = await res.json();
        setLastAction(data.last_action_time);
      }
    } catch (error) {
      console.error('Error fetching last action:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLastAction();
  }, [selectedStore]);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
        <h3 className="text-xl font-bold text-blue-800 mb-2">Autonomous Actions Timeline</h3>
        <p className="text-blue-700 mb-4">Track the latest autonomous actions by store</p>
        
        <div className="flex gap-4 items-end mb-4">
          <div>
            <label className="block text-sm font-medium text-blue-800 mb-2">Select Store</label>
            <select 
              value={selectedStore} 
              onChange={(e) => setSelectedStore(e.target.value)}
              className="px-3 py-2 border border-blue-300 rounded-lg bg-white text-black"
            >
              <option value="all">All Stores</option>
              <option value="S001">Store S001</option>
              <option value="S002">Store S002</option>
              <option value="S003">Store S003</option>
            </select>
          </div>
          <button 
            onClick={fetchLastAction} 
            disabled={loading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
        
        {lastAction ? (
          <div className="bg-white p-4 rounded-lg border border-blue-200">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                <polyline points="12,6 12,12 16,14" strokeWidth="2"/>
              </svg>
              <div>
                <p className="font-semibold text-blue-800">Last Autonomous Action</p>
                <p className="text-blue-600">{formatTimeAgo(lastAction)}</p>
                <p className="text-sm text-gray-500">Store: {selectedStore === 'all' ? 'All Stores' : selectedStore}</p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-blue-600">No autonomous actions found</p>
        )}
      </div>
    </div>
  );
};

// Auto Purchase Orders Tab Component
const AutoPurchaseOrdersTab = () => {
  const [selectedStore, setSelectedStore] = useState('');
  const [maxCost, setMaxCost] = useState(50000);
  const [approvalThreshold, setApprovalThreshold] = useState(5000);
  const [loading, setLoading] = useState(false);
  
  const handleCreatePO = async () => {
    if (!selectedStore) {
      alert('Please select a store');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${HITL_BASE_URL}/api/purchase-orders/auto-generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          store_id: selectedStore,
          max_total_cost: maxCost,
          require_approval_threshold: approvalThreshold
        })
      });
      const data = await res.json();
      alert(data.message || 'Purchase orders created successfully');
    } catch (error) {
      alert('Failed to create purchase orders');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
        <h3 className="text-xl font-bold text-purple-800 mb-2">Automated Purchase Orders</h3>
        <p className="text-purple-700 mb-4">Automatically create purchase orders for low-stock items by store</p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-purple-800 mb-2">Store ID *</label>
            <select 
              value={selectedStore} 
              onChange={(e) => setSelectedStore(e.target.value)}
              className="w-full px-3 py-2 border border-purple-300 rounded-lg bg-white text-black"
            >
              <option value="">Select Store</option>
              <option value="S001">Store S001</option>
              <option value="S002">Store S002</option>
              <option value="S003">Store S003</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-purple-800 mb-2">Max Total Cost ($)</label>
            <input 
              type="number" 
              value={maxCost}
              onChange={(e) => setMaxCost(Number(e.target.value))}
              className="w-full px-3 py-2 border border-purple-300 rounded-lg text-black"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-purple-800 mb-2">Approval Threshold ($)</label>
            <input 
              type="number" 
              value={approvalThreshold}
              onChange={(e) => setApprovalThreshold(Number(e.target.value))}
              className="w-full px-3 py-2 border border-purple-300 rounded-lg text-black"
            />
          </div>
        </div>
        
        <button 
          onClick={handleCreatePO} 
          disabled={loading || !selectedStore}
          className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Creating Purchase Orders...' : 'Create Purchase Orders'}
        </button>
        
        <div className="mt-4 text-sm text-purple-600">
          <p>• Orders above threshold will require HITL approval</p>
          <p>• Orders below threshold will be auto-approved</p>
          <p>• Store selection is required</p>
        </div>
      </div>
    </div>
  );
};

export default function HITLTab() {
  const [approvals, setApprovals] = useState<PendingApproval[]>([]);
  const [stats, setStats] = useState<ApprovalStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedApproval, setSelectedApproval] = useState<PendingApproval | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewerName, setReviewerName] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0); // 0 = Pending, 1 = Approved, 2 = Rejected, 3 = Excel Export, 4 = Hallucination Detection, 5 = Autonomous Actions, 6 = Auto Purchase Orders
//   const [selectedStore, setSelectedStore] = useState<string>("all");
//   const [lastAutonomousAction, setLastAutonomousAction] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({ open: false, message: "", severity: "info" });

  const fetchApprovals = useCallback(async (status: string = "pending") => {
    setLoading(true);
    try {
      // Use the correct endpoint with status query parameter
      const endpoint = `${HITL_BASE_URL}/api/agents/approvals/all?status=${status}`;

      console.log(`[HITL] Fetching ${status} approvals from:`, endpoint);
      const res = await fetch(endpoint);

      if (!res.ok) {
        console.error(`[HITL] HTTP error! status: ${res.status}`);
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      console.log(`[HITL] ${status} approvals raw response:`, data);

      // Extract approvals from response and ensure they match the requested status
      let approvalsData = data.approvals || data.pending_approvals || data.data || [];

      // Double-check filtering on client side to ensure consistency
      if (Array.isArray(approvalsData)) {
        approvalsData = approvalsData.filter((approval: PendingApproval) => {
          const approvalStatus = approval.status || "pending";
          return approvalStatus === status;
        });
      }

      console.log(`[HITL] Filtered ${status} approvals (${approvalsData.length} items):`, approvalsData);
      setApprovals(approvalsData);
    } catch (error: any) {
      console.error(`[HITL] Error fetching ${status} approvals:`, error);
      setSnackbar({
        open: true,
        message: `Failed to load ${status} approvals`,
        severity: "error",
      });
      setApprovals([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      console.log("[HITL] Fetching stats from individual endpoints...");

      // Fetch counts from each specific endpoint
      

      const [pendingRes, approvedRes, rejectedRes] = await Promise.all([
        fetch(`${HITL_BASE_URL}/api/agents/approvals/all?status=pending`),
        fetch(`${HITL_BASE_URL}/api/agents/approvals/all?status=approved`),
        fetch(`${HITL_BASE_URL}/api/agents/approvals/all?status=rejected`)
      ]);

      let pending = 0;
      let approved = 0;
      let rejected = 0;

      // Parse pending count
      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();
        const pendingApprovals = pendingData.approvals || pendingData.pending_approvals || pendingData.data || [];
        pending = Array.isArray(pendingApprovals) ? pendingApprovals.filter((a: PendingApproval) =>
          !a.status || a.status === "pending"
        ).length : 0;
      }

      // Parse approved count
      if (approvedRes.ok) {
        const approvedData = await approvedRes.json();
        const approvedApprovals = approvedData.approvals || approvedData.pending_approvals || approvedData.data || [];
        approved = Array.isArray(approvedApprovals) ? approvedApprovals.filter((a: PendingApproval) =>
          a.status === "approved"
        ).length : 0;
      }

      // Parse rejected count
      if (rejectedRes.ok) {
        const rejectedData = await rejectedRes.json();
        const rejectedApprovals = rejectedData.approvals || rejectedData.pending_approvals || rejectedData.data || [];
        rejected = Array.isArray(rejectedApprovals) ? rejectedApprovals.filter((a: PendingApproval) =>
          a.status === "rejected"
        ).length : 0;
      }

      const total = pending + approved + rejected;
      const approval_rate = total > 0 ? (approved / total) * 100 : 0;

      const calculatedStats = {
        pending,
        approved,
        rejected,
        total_decisions: total,
        approval_rate
      };

      console.log("[HITL] Calculated stats:", calculatedStats);
      setStats(calculatedStats);
    } catch (error: any) {
      console.error("[HITL] Error fetching stats:", error);
      // Set default stats
      setStats({
        pending: 0,
        approved: 0,
        rejected: 0,
        total_decisions: 0,
        approval_rate: 0
      });
    }
  }, []);

  // Single useEffect to handle tab changes and data fetching
  useEffect(() => {
    // Fetch based on current tab
    if (currentTab === 0) {
      fetchApprovals("pending");
    } else if (currentTab === 1) {
      fetchApprovals("approved");
    } else if (currentTab === 2) {
      fetchApprovals("rejected");
    }
  }, [currentTab, fetchApprovals]);

  // Separate useEffect for initial load and auto-refresh
  useEffect(() => {
    // Initial fetch
    fetchStats();

    // Auto-refresh interval only for pending tab
    const interval = setInterval(() => {
      // Only auto-refresh if we're on the pending tab
      if (currentTab === 0) {
        fetchApprovals("pending");
        fetchStats();
      }
    }, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [fetchApprovals, fetchStats, currentTab]);

  const handleApprovalClick = (approval: PendingApproval) => {
    setSelectedApproval(approval);
    setDialogOpen(true);
    setReviewNotes("");
  };

  const handleApprove = async () => {
    if (!selectedApproval) return;
    if (!reviewerName.trim()) {
      setSnackbar({
        open: true,
        message: "Please enter your name",
        severity: "warning",
      });
      return;
    }

    setActionLoading(true);
    try {
      const res = await fetch(
        `${HITL_BASE_URL}/api/agents/approvals/${selectedApproval.approval_id}/approve`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            decision: "APPROVE",
            review_notes: reviewNotes || "Approved",
            reviewed_by: reviewerName,
          }),
        }
      );

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      const data = await res.json();
      setSnackbar({
        open: true,
        message: data.message || "Request approved successfully",
        severity: "success",
      });
      setDialogOpen(false);
      // Immediately update stats and refresh data
      await fetchStats();
      if (currentTab === 0) {
        fetchApprovals("pending");
      } else if (currentTab === 1) {
        fetchApprovals("approved");
      } else if (currentTab === 2) {
        fetchApprovals("rejected");
      }
    } catch (error: any) {
      console.error("[HITL] Error approving request:", error);
      setSnackbar({
        open: true,
        message: "Failed to approve request",
        severity: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedApproval) return;
    if (!reviewerName.trim()) {
      setSnackbar({
        open: true,
        message: "Please enter your name",
        severity: "warning",
      });
      return;
    }
    if (!reviewNotes.trim()) {
      setSnackbar({
        open: true,
        message: "Please provide a reason for rejection",
        severity: "warning",
      });
      return;
    }

    setActionLoading(true);
    try {
      const res = await fetch(
        `${HITL_BASE_URL}/api/agents/approvals/${selectedApproval.approval_id}/reject`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            decision: "REJECT",
            review_notes: reviewNotes,
            reviewed_by: reviewerName,
          }),
        }
      );

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      const data = await res.json();
      setSnackbar({
        open: true,
        message: data.message || "Request rejected successfully",
        severity: "success",
      });
      setDialogOpen(false);
      // Immediately update stats and refresh data
      await fetchStats();
      if (currentTab === 0) {
        fetchApprovals("pending");
      } else if (currentTab === 1) {
        fetchApprovals("approved");
      } else if (currentTab === 2) {
        fetchApprovals("rejected");
      }
    } catch (error: any) {
      console.error("[HITL] Error rejecting request:", error);
      setSnackbar({
        open: true,
        message: "Failed to reject request",
        severity: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-8">
      {/* Header */}
      <div className="bg-white p-4 md:p-6 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-xl md:text-3xl font-bold text-slate-900 flex items-center gap-2 md:gap-3">
              <svg
                className="w-6 h-6 md:w-8 md:h-8 text-purple-600"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Human-in-the-Loop Approvals
            </h2>
            <p className="mt-1 md:mt-2 text-sm md:text-base text-slate-600">
              Review and approve agent-proposed actions
            </p>
          </div>
          <button
            onClick={() => {
              // Refresh based on current tab
              if (currentTab === 0) {
                fetchApprovals("pending");
              } else if (currentTab === 1) {
                fetchApprovals("approved");
              } else if (currentTab === 2) {
                fetchApprovals("rejected");
              } else {
                fetchApprovals("pending");
              }
              fetchStats();
            }}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-4 py-2 font-medium text-sm shadow-sm transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M4 4v5h.582M20 20v-5h-.581" strokeWidth="2" />
              <path
                d="M5.002 9A7.003 7.003 0 0 1 12 5c2.21 0 4.21.896 5.657 2.343M18.998 15A7.003 7.003 0 0 1 12 19c-2.21 0-4.21-.896-5.657-2.343"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200 rounded-xl p-4">
            <div className="text-sm font-medium text-yellow-800">Pending Approvals</div>
            <div className="text-3xl font-bold text-yellow-900 mt-2">{stats.pending}</div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-4">
            <div className="text-sm font-medium text-green-800">Approved</div>
            <div className="text-3xl font-bold text-green-900 mt-2">{stats.approved}</div>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-xl p-4">
            <div className="text-sm font-medium text-red-800">Rejected</div>
            <div className="text-3xl font-bold text-red-900 mt-2">{stats.rejected}</div>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-4">
            <div className="text-sm font-medium text-blue-800">Approval Rate</div>
            <div className="text-3xl font-bold text-blue-900 mt-2">
              {stats.approval_rate ? `${Math.round(stats.approval_rate)}%` : "N/A"}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)} variant="scrollable" scrollButtons="auto">
            <Tab label={`Pending (${stats?.pending || 0})`} />
            <Tab label={`Approved (${stats?.approved || 0})`} />
            <Tab label={`Rejected (${stats?.rejected || 0})`} />
            {/* <Tab label="Excel Export" />
            <Tab label="Hallucination Detection" />
            <Tab label="Autonomous Actions" />
            <Tab label="Auto Purchase Orders" /> */}
          </Tabs>
        </Box>

        {/* Tab Content */}
        <div className="p-4 md:p-6">
          {currentTab === 3 && <ExcelExportTab />}
          {currentTab === 4 && <HallucinationDetectionTab />}
          {currentTab === 5 && <AutonomousActionsTab />}
          {currentTab === 6 && <AutoPurchaseOrdersTab />}
          
          {/* Original Approvals Content */}
          {(currentTab === 0 || currentTab === 1 || currentTab === 2) && (
            loading ? (
            <div className="flex justify-center items-center py-12">
              <CircularProgress />
            </div>
          ) : approvals.length === 0 ? (
            <div className="text-center py-12">
              <svg
                className="w-16 h-16 mx-auto text-slate-300"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  strokeWidth="2"
                />
              </svg>
              <p className="mt-4 text-slate-500">No approvals to display</p>
            </div>
          ) : (
            <div className="space-y-4">
              {approvals.map((approval) => (
                <div
                  key={approval.approval_id}
                  className="border border-slate-200 rounded-xl p-4 hover:border-purple-300 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => handleApprovalClick(approval)}
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <h3 className="text-lg font-semibold text-slate-900">
                          {approval.agent_name}
                        </h3>
                        <ImpactBadge level={approval.impact_level} />
                        <PriorityBadge priority={approval.priority} />
                        {approval.status === "approved" && (
                          <Chip label="APPROVED" size="small" color="success" />
                        )}
                        {approval.status === "rejected" && (
                          <Chip label="REJECTED" size="small" color="error" />
                        )}
                      </div>

                      <div className="space-y-2">
                        <div>
                          <span className="text-sm font-medium text-slate-700">Action: </span>
                          <span className="text-sm text-slate-600">{approval.action_type}</span>
                        </div>

                        {approval.reasoning && (
                          <div>
                            <span className="text-sm font-medium text-slate-700">
                              Reasoning:{" "}
                            </span>
                            <span className="text-sm text-slate-600">{approval.reasoning}</span>
                          </div>
                        )}

                        {approval.estimated_cost && (
                          <div>
                            <span className="text-sm font-medium text-slate-700">
                              Estimated Cost:{" "}
                            </span>
                            <span className="text-sm text-slate-900 font-semibold">
                              ${approval.estimated_cost.toLocaleString()}
                            </span>
                          </div>
                        )}

                        <div className="max-w-xs">
                          <ConfidenceBar score={approval.confidence_score} />
                        </div>

                        <div className="text-xs text-slate-500">
                          Created {formatTimeAgo(approval.created_at)}
                        </div>

                        {approval.reviewed_by && approval.reviewed_at && (
                          <div className="text-xs text-slate-500 mt-2 p-2 bg-slate-50 rounded">
                            Reviewed by {approval.reviewed_by} on{" "}
                            {formatTimeAgo(approval.reviewed_at)}
                            {approval.review_notes && (
                              <div className="mt-1">
                                <span className="font-medium">Notes: </span>
                                {approval.review_notes}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* {(approval.status === "pending" || !approval.status) && (
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApprovalClick(approval);
                          }}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                        >
                          Approve
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApprovalClick(approval);
                          }}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                        >
                          Reject
                        </button>
                      </div>
                    )} */}
                  </div>
                </div>
              ))}
            </div>
          )
          )}
        </div>
      </div>

      {/* Approval Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <div className="flex items-center gap-3">
            <svg
              className="w-6 h-6 text-purple-600"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                strokeWidth="2"
              />
            </svg>
            <div>
              <div className="font-semibold flex items-center gap-2">
                Review Approval Request
                {selectedApproval?.status === "approved" && (
                  <Chip label="APPROVED" size="small" color="success" />
                )}
                {selectedApproval?.status === "rejected" && (
                  <Chip label="REJECTED" size="small" color="error" />
                )}
                {selectedApproval?.status === "pending" && (
                  <Chip label="PENDING" size="small" color="warning" />
                )}
              </div>
              {selectedApproval && (
                <div className="text-sm text-slate-500 mt-0.5">
                  {selectedApproval.approval_id}
                </div>
              )}
            </div>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {selectedApproval && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium text-slate-700">Agent</div>
                  <div className="text-base text-slate-900">{selectedApproval.agent_name}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-700">Action Type</div>
                  <div className="text-base text-slate-900">{selectedApproval.action_type}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-700">Impact Level</div>
                  <ImpactBadge level={selectedApproval.impact_level} />
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-700">Priority</div>
                  <PriorityBadge priority={selectedApproval.priority} />
                </div>
              </div>

              <div>
                <div className="text-sm font-medium text-slate-700 mb-2">Confidence Score</div>
                <ConfidenceBar score={selectedApproval.confidence_score} />
              </div>

              {selectedApproval.reasoning && (
                <div>
                  <div className="text-sm font-medium text-slate-700 mb-2">Agent Reasoning</div>
                  <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700">
                    {selectedApproval.reasoning}
                  </div>
                </div>
              )}

              {selectedApproval.estimated_cost && (
                <div>
                  <div className="text-sm font-medium text-slate-700 mb-2">Estimated Cost</div>
                  <div className="text-2xl font-bold text-slate-900">
                    ${selectedApproval.estimated_cost.toLocaleString()}
                  </div>
                </div>
              )}

              {selectedApproval.proposed_action && (
                <div>
                  <div className="text-sm font-medium text-slate-700 mb-2">
                    Proposed Action Details
                  </div>
                  <div className="bg-slate-900 text-slate-100 rounded-lg p-3 text-xs font-mono overflow-auto max-h-60">
                    <pre>{JSON.stringify(selectedApproval.proposed_action, null, 2)}</pre>
                  </div>
                </div>
              )}

              {selectedApproval.status === "pending" && (
                <>
                  <TextField
                    fullWidth
                    label="Your Name"
                    value={reviewerName}
                    onChange={(e) => setReviewerName(e.target.value)}
                    size="small"
                    required
                    helperText="Enter your name to proceed with review"
                  />

                  <TextField
                    fullWidth
                    label="Review Notes"
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    multiline
                    rows={3}
                    size="small"
                    placeholder="Add notes about your decision (required for rejection)"
                  />
                </>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={actionLoading}>
            {selectedApproval?.status === "pending" ? "Cancel" : "Close"}
          </Button>
          {selectedApproval?.status === "pending" && (
            <>
              <Button
                onClick={handleReject}
                color="error"
                variant="contained"
                disabled={actionLoading}
                startIcon={
                  actionLoading ? <CircularProgress size={16} /> : null
                }
              >
                Reject
              </Button>
              <Button
                onClick={handleApprove}
                color="success"
                variant="contained"
                disabled={actionLoading}
                startIcon={
                  actionLoading ? <CircularProgress size={16} /> : null
                }
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}